import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Box, Container, Divider, Link, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';

import LogoSVG from '../../assets/icons/LogoSVG';

import './styles.scss';

// ── Constants ─────────────────────────────────────────────────────────────────

const LAST_UPDATED   = 'March 28, 2026';
const CONTACT_EMAIL  = 'contact@palindev.com';
const SITE_URL       = 'https://notecore.app';

// ── TOC sections ─────────────────────────────────────────────────────────────

const TOC = [
  { id: 's1',  n: '1',  title: 'Acceptance of Terms'           },
  { id: 's2',  n: '2',  title: 'Definitions'                    },
  { id: 's3',  n: '3',  title: 'Eligibility'                    },
  { id: 's4',  n: '4',  title: 'Account Registration'           },
  { id: 's5',  n: '5',  title: 'Description of Service'         },
  { id: 's6',  n: '6',  title: 'Subscription Plans & Payments'  },
  { id: 's7',  n: '7',  title: 'No Refund Policy'               },
  { id: 's8',  n: '8',  title: 'User Content'                   },
  { id: 's9',  n: '9',  title: 'Prohibited Conduct'             },
  { id: 's10', n: '10', title: 'Intellectual Property'          },
  { id: 's11', n: '11', title: 'Privacy'                        },
  { id: 's12', n: '12', title: 'Third-Party Services'           },
  { id: 's13', n: '13', title: 'Disclaimer of Warranties'       },
  { id: 's14', n: '14', title: 'Limitation of Liability'        },
  { id: 's15', n: '15', title: 'Indemnification'                },
  { id: 's16', n: '16', title: 'Modifications'                  },
  { id: 's17', n: '17', title: 'Termination'                    },
  { id: 's18', n: '18', title: 'Governing Law & Disputes'       },
  { id: 's19', n: '19', title: 'Miscellaneous'                  },
  { id: 's20', n: '20', title: 'Contact Information'            },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionHeading = ({ id, n, title }) => (
  <Typography
    id={id}
    variant="h6"
    className="tos-section-heading"
    sx={{ mt: 5, mb: 1.5, fontWeight: 800, fontSize: { xs: 16, sm: 18 }, scrollMarginTop: '90px' }}
  >
    <Box component="span" className="tos-section-number">{n}.</Box>
    {' '}{title}
  </Typography>
);

const P = ({ children, ...props }) => (
  <Typography
    variant="body2"
    className="tos-paragraph"
    sx={{ mb: 1.5, lineHeight: 1.85, fontSize: { xs: 14, sm: 15 }, ...props.sx }}
    {...props}
  >
    {children}
  </Typography>
);

const Sub = ({ n, children }) => (
  <Box className="tos-sub-item">
    <Typography variant="body2" component="span" className="tos-sub-number">{n}</Typography>
    <Typography variant="body2" component="span" className="tos-sub-text" sx={{ lineHeight: 1.85, fontSize: { xs: 14, sm: 15 } }}>
      {children}
    </Typography>
  </Box>
);

const BulletList = ({ items }) => (
  <Box component="ul" className="tos-bullet-list">
    {items.map((item, i) => (
      <Box key={i} component="li" className="tos-bullet-item">
        <Typography variant="body2" sx={{ lineHeight: 1.85, fontSize: { xs: 14, sm: 15 } }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

const PlanBox = ({ name, price, badge, items }) => (
  <Box className="tos-plan-box">
    <Box className="tos-plan-box__header">
      <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: 15 }}>{name}</Typography>
      {badge && <Box component="span" className="tos-plan-badge">{badge}</Box>}
    </Box>
    <Typography variant="body2" className="tos-plan-price">{price}</Typography>
    <BulletList items={items} />
  </Box>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const TermsOfServicePage = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service — NoteCORE</title>
        <meta name="description" content="Read the NoteCORE Terms of Service. Understand your rights and obligations when using the NoteCORE writing workspace, including plan details, payment terms, refund policy, and privacy rights." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/terms`} />

        {/* Open Graph */}
        <meta property="og:type"        content="website" />
        <meta property="og:url"         content={`${SITE_URL}/terms`} />
        <meta property="og:site_name"   content="NoteCORE" />
        <meta property="og:title"       content="Terms of Service — NoteCORE" />
        <meta property="og:description" content="Read the NoteCORE Terms of Service. Understand the rules, payment terms, no-refund policy, and your rights as a user of the NoteCORE writing workspace." />
        <meta property="og:image"       content={`${SITE_URL}/og.png`} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="Terms of Service — NoteCORE" />
        <meta name="twitter:description" content="Read the NoteCORE Terms of Service covering plans, payments, refund policy, and user rights." />
        <meta name="twitter:image"       content={`${SITE_URL}/og.png`} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Terms of Service — NoteCORE',
            description: 'Terms of Service for NoteCORE, the focused writing workspace by Palindev.',
            url: `${SITE_URL}/terms`,
            dateModified: LAST_UPDATED,
            inLanguage: 'en',
            isPartOf: {
              '@type': 'WebSite',
              name: 'NoteCORE',
              url: SITE_URL,
            },
            publisher: {
              '@type': 'Person',
              name: 'Palindev',
              email: CONTACT_EMAIL,
              url: 'https://palindev.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
                { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${SITE_URL}/terms` },
              ],
            },
          })}
        </script>
      </Helmet>

      <Box className="tos-root">

        {/* ── Top bar ── */}
        <Box component="header" className="tos-header">
          <Container maxWidth="lg">
            <Box className="tos-header__inner">
              <RouterLink to="/" className="tos-header__logo">
                <LogoSVG height="26px" />
              </RouterLink>
              <Link
                component={RouterLink}
                to="/"
                className="tos-back-link"
                underline="none"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 13 }} />
                Back to Home
              </Link>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" className="tos-container">
          <Box className="tos-layout">

            {/* ── Sidebar TOC (desktop) ── */}
            <Box component="nav" className="tos-toc" aria-label="Table of contents">
              <Typography variant="overline" className="tos-toc__label">
                Contents
              </Typography>
              <Box component="ol" className="tos-toc__list">
                {TOC.map(({ id, n, title }) => (
                  <Box
                    key={id}
                    component="li"
                    className="tos-toc__item"
                    onClick={() => scrollTo(id)}
                  >
                    <Box component="span" className="tos-toc__num">{n}.</Box>
                    {title}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ── Main content ── */}
            <Box component="main" className="tos-content">

              {/* Page title */}
              <Box className="tos-hero">
                <Typography variant="h3" fontWeight={900} className="tos-title" sx={{ fontSize: { xs: 28, sm: 36 } }}>
                  Terms of Service
                </Typography>
                <Typography variant="body2" className="tos-meta">
                  Last updated: <strong>{LAST_UPDATED}</strong>
                </Typography>
                <Box className="tos-summary-box">
                  <Typography variant="body2" sx={{ lineHeight: 1.8, fontSize: { xs: 13, sm: 14 } }}>
                    <strong>Summary (not a substitute for the full terms):</strong> NoteCORE is developed
                    and operated by an individual doing business as <strong>Palindev</strong>. By using
                    NoteCORE, you agree to these Terms. The Service is free for up to 3 notes. A one-time
                    Pro upgrade (₹199) unlocks unlimited notes. <strong>All purchases are final — we do
                    not offer refunds.</strong> You own your content. We do not sell your data. This
                    Service is governed by the laws of India.
                  </Typography>
                </Box>
              </Box>

              <Divider className="tos-divider" sx={{ my: 4 }} />

              {/* ── Mobile TOC ── */}
              <Box className="tos-toc-mobile">
                <Typography variant="overline" className="tos-toc__label" sx={{ display: 'block', mb: 1 }}>
                  Jump to section
                </Typography>
                <Box className="tos-toc-mobile__grid">
                  {TOC.map(({ id, n, title }) => (
                    <Box key={id} className="tos-toc-mobile__item" onClick={() => scrollTo(id)}>
                      <Box component="span" className="tos-toc__num tos-toc__num--inline">{n}.</Box> {title}
                    </Box>
                  ))}
                </Box>
                <Divider className="tos-divider" sx={{ mt: 3, mb: 1 }} />
              </Box>

              {/* ─────────────────────────────────────────────────────────────
                  Section 1: Acceptance of Terms
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s1" n="1" title="Acceptance of Terms" />
              <P>
                These Terms of Service ("Terms") constitute a legally binding agreement between you and
                Palindev ("we", "us", "our"), the individual operator of NoteCORE, governing your access
                to and use of the NoteCORE web application and any related services (collectively, the
                "Service") available at{' '}
                <Link href={SITE_URL} target="_blank" rel="noopener noreferrer" className="tos-link">
                  {SITE_URL}
                </Link>.
              </P>
              <P>
                <strong>By accessing or using the Service in any manner, you acknowledge that you have
                read, understood, and agree to be bound by these Terms and our Privacy Policy, which is
                incorporated herein by reference.</strong> If you do not agree to all of these Terms, you
                must not access or use the Service.
              </P>
              <P>
                If you are using the Service on behalf of a third party, you represent and warrant that
                you have full authority to bind that party to these Terms.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 2: Definitions
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s2" n="2" title="Definitions" />
              <P>For the purposes of these Terms, the following definitions apply:</P>
              <BulletList items={[
                '"Service" means the NoteCORE web application, its features, content, and all related services.',
                '"User", "you", or "your" refers to any individual who accesses, browses, or uses the Service.',
                '"Account" means the user account created upon authenticating via Google OAuth.',
                '"Content" means all notes, text, data, and other information that you create, upload, or store through the Service.',
                '"Basic Plan" means the free tier of the Service permitting storage of up to three (3) notes.',
                '"Pro Plan" means the paid tier of the Service granting unlimited note storage, available via a one-time payment.',
                '"Palindev" refers to the individual operator doing business under the Palindev brand, developing and operating NoteCORE.',
              ]} />

              {/* ─────────────────────────────────────────────────────────────
                  Section 3: Eligibility
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s3" n="3" title="Eligibility" />
              <P>
                The Service is available exclusively to individuals who are at least <strong>13 years of
                age</strong>. By using the Service, you represent and warrant that you meet this age
                requirement.
              </P>
              <P>
                We do not knowingly collect or solicit personal information from individuals under the age
                of 13. If we learn that a user under 13 has registered an account, we will promptly
                terminate that account and delete all associated data without notice.
              </P>
              <P>
                By using the Service, you further represent and warrant that: (a) you are not located in
                a country subject to a government embargo; (b) you are not listed on any government
                prohibited-party list; and (c) your use of the Service complies with all laws and
                regulations applicable to you.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 4: Account Registration
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s4" n="4" title="Account Registration" />
              <Sub n="4.1">
                <strong>Google OAuth Authentication.</strong> NoteCORE uses Google OAuth 2.0 as its
                exclusive authentication method. To create an account, you must sign in with a valid
                Google account. By doing so, you authorize NoteCORE to access your Google profile
                information — specifically your name, email address, and profile picture — solely for
                account identification purposes.
              </Sub>
              <Sub n="4.2">
                <strong>Account Security.</strong> You are solely responsible for all activity that
                occurs under your account. You must immediately notify us at{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link> if
                you suspect any unauthorized access to or use of your account. We are not liable for any
                loss or damage arising from your failure to maintain the security of your account.
              </Sub>
              <Sub n="4.3">
                <strong>One Account Per User.</strong> Each individual may maintain only one active
                account. Creating multiple accounts to circumvent plan limits or any other restriction
                is prohibited and may result in immediate termination of all associated accounts.
              </Sub>
              <Sub n="4.4">
                <strong>Account Accuracy.</strong> Your account information is derived directly from
                your Google profile at the time of authentication. You are responsible for keeping your
                Google account information accurate. We have no obligation to verify the accuracy of
                information obtained via Google OAuth.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 5: Description of Service
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s5" n="5" title="Description of Service" />
              <P>
                NoteCORE is a web-based productivity application providing a rich-text block editor for
                creating, editing, and organizing personal notes, with cloud synchronization across
                sessions. The Service is offered under two plans:
              </P>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', my: 2.5 }}>
                <PlanBox
                  name="Basic Plan"
                  price="Free — No credit card required"
                  badge="Free"
                  items={[
                    'Up to 3 notes',
                    'Full rich-text block editor',
                    'All content block types',
                    'Cloud sync & storage',
                  ]}
                />
                <PlanBox
                  name="Pro Plan"
                  price="₹199 — One-time payment, lifetime access"
                  badge="Pro"
                  items={[
                    'Unlimited notes',
                    'All Basic Plan features',
                    'All future updates included',
                    'No recurring charges — ever',
                  ]}
                />
              </Box>
              <P>
                We reserve the right to modify, enhance, or discontinue any aspect of the Service at
                any time. We will make reasonable efforts to communicate significant changes that may
                affect your use of the Service, but are under no obligation to do so for minor updates
                or improvements.
              </P>
              <P>
                The Service requires an internet connection and a modern web browser. We do not
                guarantee availability of the Service at all times and may perform maintenance that
                renders the Service temporarily unavailable.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 6: Subscription Plans & Payments
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s6" n="6" title="Subscription Plans & Payments" />
              <Sub n="6.1">
                <strong>Pro Plan Purchase.</strong> The Pro Plan is available for a one-time payment
                of <strong>INR 199 (₹199)</strong> as displayed
                at checkout. Upon successful payment, Pro Plan access is activated immediately and
                permanently linked to your account.
              </Sub>
              <Sub n="6.2">
                <strong>Payment Processor.</strong> Payments are processed securely by{' '}
                <strong>Razorpay</strong>, a licensed third-party payment processor. NoteCORE does not
                store, process, or have access to your payment card details. Your payment information is
                governed by Razorpay's terms of service and privacy policy.
              </Sub>
              <Sub n="6.3">
                <strong>Authorization.</strong> By completing a purchase, you authorize us to charge the
                applicable amount to your selected payment method through the Razorpay payment interface.
                You represent that you are authorized to use the payment method provided.
              </Sub>
              <Sub n="6.4">
                <strong>Pricing.</strong> We reserve the right to change the pricing of the Pro Plan at
                any time. Any price change will be communicated in advance and will not affect users who
                have already completed a Pro Plan purchase.
              </Sub>
              <Sub n="6.5">
                <strong>Taxes &amp; Fees.</strong> Prices displayed are exclusive of any applicable
                taxes, duties, or fees, which may be added at checkout depending on your jurisdiction.
                You are solely responsible for all applicable taxes associated with your purchase.
              </Sub>
              <Sub n="6.6">
                <strong>Currency.</strong> The listed price is in Indian Rupees (INR). Payments are
                processed through Razorpay. Your financial institution may apply its own exchange rate
                or transaction fees if you are paying from outside India.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 7: No Refund Policy
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s7" n="7" title="No Refund Policy" />
              <Box className="tos-warning-box">
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.8 }}>
                  ALL PURCHASES ARE FINAL AND NON-REFUNDABLE.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: 13, sm: 14 }, lineHeight: 1.8 }}>
                  Given the nature of NoteCORE as a digital software service, all Pro Plan purchases
                  are final. We do not offer refunds, exchanges, cancellations, or credits for any
                  reason, except as expressly required by mandatory consumer protection law applicable
                  in your jurisdiction.
                </Typography>
              </Box>
              <P sx={{ mt: 2 }}>
                By completing a purchase, you acknowledge and agree that:
              </P>
              <BulletList items={[
                'You have reviewed the Service and the plan features before purchasing.',
                'You understand that the Pro Plan is a one-time digital purchase and not a subscription.',
                'You waive any right of withdrawal or cancellation to the extent permitted by applicable law.',
              ]} />
              <P>
                If you experience a technical issue that prevents access to Pro features{' '}
                <strong>immediately after a confirmed payment</strong>, contact us at{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link> within{' '}
                <strong>48 hours</strong> of the transaction. We will investigate and work to resolve
                verified technical failures on a case-by-case basis. This is not a guarantee of a
                refund; it is a commitment to resolve service delivery failures.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 8: User Content
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s8" n="8" title="User Content" />
              <Sub n="8.1">
                <strong>Ownership.</strong> You retain full ownership of all Content you create through
                the Service. Nothing in these Terms transfers any intellectual property rights in your
                Content to us.
              </Sub>
              <Sub n="8.2">
                <strong>License to Operate.</strong> By using the Service, you grant Palindev a limited,
                non-exclusive, worldwide, royalty-free license to store, process, cache, and transmit
                your Content exclusively for the purpose of operating and providing the Service to you.
                This license terminates when you delete your Content or close your account.
              </Sub>
              <Sub n="8.3">
                <strong>No Review of Content.</strong> We do not routinely review, monitor, or verify
                your Content. You are solely responsible for all Content you create or store through
                the Service and for ensuring that such Content complies with these Terms and all
                applicable laws.
              </Sub>
              <Sub n="8.4">
                <strong>Content Limits.</strong> Basic Plan users may store a maximum of three (3) notes.
                Pro Plan users have no limit on the number of notes. We reserve the right to establish
                reasonable limits on storage size per note.
              </Sub>
              <Sub n="8.5">
                <strong>Data Loss.</strong> While we implement reasonable measures to protect your
                Content, we are not responsible for any loss, corruption, or deletion of your Content.
                We strongly recommend maintaining independent backups of any important notes.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 9: Prohibited Conduct
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s9" n="9" title="Prohibited Conduct" />
              <P>You agree not to use the Service to:</P>
              <BulletList items={[
                'Violate any applicable local, national, or international law or regulation.',
                'Create, upload, or transmit any Content that is unlawful, harmful, defamatory, obscene, fraudulent, infringing, or otherwise objectionable.',
                'Gain unauthorized access to any part of the Service, its servers, or any account other than your own.',
                'Reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service.',
                'Circumvent, disable, or interfere with any security-related features of the Service.',
                'Use automated scripts, bots, crawlers, or scrapers to access or collect data from the Service.',
                'Impose an unreasonable or disproportionately large load on the Service infrastructure.',
                'Transmit any malware, viruses, Trojan horses, or other harmful or disruptive code.',
                'Create multiple accounts to circumvent plan limits or other restrictions.',
                'Impersonate any person or entity, or falsely represent your affiliation with any person or entity.',
                'Use the Service for commercial purposes unless expressly authorized in writing by us.',
              ]} />
              <P>
                Violation of this section may result in immediate suspension or permanent termination of
                your account, without prior notice or liability to you. We reserve the right to report
                any illegal activity to the appropriate authorities.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 10: Intellectual Property
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s10" n="10" title="Intellectual Property" />
              <Sub n="10.1">
                <strong>Our Property.</strong> The Service, including but not limited to its software
                code, user interface, visual design, logo, trade names, brand identifiers, and
                documentation, is the exclusive intellectual property of Palindev and is protected by
                applicable copyright, trademark, and other intellectual property laws of India and
                international treaties.
              </Sub>
              <Sub n="10.2">
                <strong>Limited License.</strong> Subject to your compliance with these Terms, we grant
                you a limited, non-exclusive, non-transferable, revocable license to access and use the
                Service solely for your personal, non-commercial note-taking purposes.
              </Sub>
              <Sub n="10.3">
                <strong>Restrictions.</strong> You may not reproduce, distribute, modify, create
                derivative works from, publicly display, publicly perform, republish, or otherwise
                exploit any part of the Service or its content without our prior written consent.
              </Sub>
              <Sub n="10.4">
                <strong>Feedback.</strong> If you submit feedback, suggestions, or ideas regarding the
                Service, you grant us an irrevocable, perpetual, royalty-free right to use such
                feedback for any purpose without compensation or attribution to you.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 11: Privacy
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s11" n="11" title="Privacy" />
              <P>
                Your privacy is important to us. Our Privacy Policy, available at{' '}
                <Link href={`${SITE_URL}/privacy`} className="tos-link" component={RouterLink} to="/privacy">
                  {SITE_URL}/privacy
                </Link>
                , describes how we collect, use, store, and share your personal information. The Privacy
                Policy is incorporated into these Terms by reference and forms part of this agreement.
              </P>
              <P>
                By using the Service, you consent to the collection and processing of your information
                as described in the Privacy Policy. In summary, we collect:
              </P>
              <BulletList items={[
                'Google account profile data (name, email address, profile picture) — for authentication and account identification.',
                'Note content and metadata — stored securely in our database to provide the Service.',
                'Payment transaction records — maintained for billing and legal compliance purposes.',
              ]} />
              <P>
                <strong>We do not sell, rent, or trade your personal information to third parties for
                marketing purposes.</strong>
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 12: Third-Party Services
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s12" n="12" title="Third-Party Services" />
              <P>
                The Service integrates with and relies on the following third-party services:
              </P>
              <BulletList items={[
                'Google OAuth 2.0 — for secure user authentication. Your use is subject to Google\'s Terms of Service and Privacy Policy.',
                'Razorpay — for processing Pro Plan payments. Your payment information is subject to Razorpay\'s Terms of Service and Privacy Policy.',
                'Railway — infrastructure and application hosting provider.',
                'Neon — managed PostgreSQL database hosting.',
                'Netlify — frontend deployment and content delivery.',
              ]} />
              <P>
                We are not responsible for the practices, availability, or content of these third-party
                services. Any issues arising from your use of these services should be directed to the
                respective providers. We recommend reviewing the terms and privacy policies of each
                third-party service before using the Service.
              </P>
              <P>
                The Service may contain links to third-party websites. Such links are provided for
                convenience only. We do not endorse and are not responsible for the content or
                practices of any linked websites.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 13: Disclaimer of Warranties
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s13" n="13" title="Disclaimer of Warranties" />
              <Box className="tos-caps-box">
                <P>
                  THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES
                  OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE
                  LAW, PALINDEV EXPRESSLY DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED
                  WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND
                  ANY WARRANTIES ARISING FROM COURSE OF DEALING OR COURSE OF PERFORMANCE.
                </P>
                <P sx={{ mb: 0 }}>
                  WE DO NOT WARRANT THAT: (A) THE SERVICE WILL MEET YOUR REQUIREMENTS; (B) THE SERVICE
                  WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; (C) THE RESULTS OBTAINED FROM
                  THE USE OF THE SERVICE WILL BE ACCURATE OR RELIABLE; (D) ANY ERRORS IN THE SERVICE
                  WILL BE CORRECTED; OR (E) THE SERVICE OR ITS SERVERS ARE FREE OF VIRUSES OR OTHER
                  HARMFUL COMPONENTS.
                </P>
              </Box>
              <P sx={{ mt: 2 }}>
                Use of the Service is at your sole risk. You are responsible for any damage to your
                device or loss of data that results from use of the Service.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 14: Limitation of Liability
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s14" n="14" title="Limitation of Liability" />
              <Box className="tos-caps-box">
                <P>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL PALINDEV, ITS
                  INDIVIDUAL OPERATOR, OR ANY OF ITS AFFILIATES, SERVICE PROVIDERS, OR LICENSORS BE
                  LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
                  DAMAGES — INCLUDING WITHOUT LIMITATION LOSS OF PROFITS, DATA, CONTENT, GOODWILL, OR
                  OTHER INTANGIBLE LOSSES — ARISING OUT OF OR IN CONNECTION WITH: (A) YOUR ACCESS TO OR
                  USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICE; (B) ANY CONDUCT OR CONTENT OF ANY
                  THIRD PARTY ON THE SERVICE; (C) ANY CONTENT OBTAINED FROM THE SERVICE; OR (D)
                  UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, EVEN IF WE
                  HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND REGARDLESS OF THE THEORY OF
                  LIABILITY.
                </P>
                <P sx={{ mb: 0 }}>
                  IN ANY CASE, OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
                  RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL
                  AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE CLAIM, OR
                  (B) INR 500 (₹500).
                </P>
              </Box>
              <P sx={{ mt: 2 }}>
                Some jurisdictions do not allow the exclusion or limitation of certain damages. In such
                jurisdictions, our liability will be limited to the maximum extent permitted by law.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 15: Indemnification
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s15" n="15" title="Indemnification" />
              <P>
                You agree to indemnify, defend, and hold harmless Palindev and its individual operator
                from and against any and all claims, liabilities, damages, losses, costs, and expenses
                (including reasonable legal fees) arising out of or in any way connected with:
              </P>
              <BulletList items={[
                'Your access to or use of the Service.',
                'Your violation of any provision of these Terms.',
                'Your Content or your infringement of any intellectual property or other right of any person or entity.',
                'Your violation of any applicable law or regulation.',
                'Any misrepresentation made by you in connection with the Service.',
              ]} />
              <P>
                We reserve the right, at our own expense, to assume the exclusive defense and control of
                any matter subject to indemnification by you, in which case you agree to cooperate with
                our defense of such claims.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 16: Modifications
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s16" n="16" title="Modifications" />
              <Sub n="16.1">
                <strong>Modifications to the Service.</strong> We reserve the right to modify, enhance,
                suspend, or discontinue the Service (or any part thereof) at any time, with or without
                notice. We shall not be liable to you or any third party for any such modification,
                suspension, or discontinuation.
              </Sub>
              <Sub n="16.2">
                <strong>Modifications to These Terms.</strong> We may revise these Terms at any time by
                posting the updated Terms on this page with a new "Last Updated" date. For material
                changes, we will make reasonable efforts to provide notice (such as displaying a
                prominent notice on the Service or sending an email to your registered address).
              </Sub>
              <Sub n="16.3">
                <strong>Your Acceptance.</strong> Your continued use of the Service after any updated
                Terms are posted constitutes your binding acceptance of the revised Terms. If you do not
                agree to the revised Terms, you must discontinue use of the Service immediately.
              </Sub>
              <Sub n="16.4">
                <strong>Your Responsibility.</strong> It is your responsibility to review these Terms
                periodically. The most current version will always be available at{' '}
                <Link href={`${SITE_URL}/terms`} className="tos-link" component={RouterLink} to="/terms">
                  {SITE_URL}/terms
                </Link>.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 17: Termination
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s17" n="17" title="Termination" />
              <Sub n="17.1">
                <strong>Duration.</strong> These Terms remain in effect for the duration of your use of
                the Service, unless terminated earlier in accordance with this section.
              </Sub>
              <Sub n="17.2">
                <strong>Termination by You.</strong> You may terminate your account at any time by
                contacting us at{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>.
                Termination does not entitle you to any refund of amounts previously paid.
              </Sub>
              <Sub n="17.3">
                <strong>Termination by Us.</strong> We may suspend or permanently terminate your access
                to the Service at our sole discretion, without prior notice or liability, for any
                reason including but not limited to: your breach of these Terms, conduct that we
                determine is harmful to other users, the Service, or third parties, or if required by
                applicable law.
              </Sub>
              <Sub n="17.4">
                <strong>Effect of Termination.</strong> Upon termination of your account for any
                reason: (a) all rights and licenses granted to you under these Terms immediately
                terminate; (b) you must cease all use of the Service; and (c) you may lose access to
                your Content. We are not liable for any loss of Content resulting from account
                termination. Provisions of these Terms that by their nature should survive termination
                — including Sections 8, 10, 13, 14, 15, 18, and 19 — shall survive.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 18: Governing Law & Disputes
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s18" n="18" title="Governing Law & Disputes" />
              <Sub n="18.1">
                <strong>Governing Law.</strong> These Terms and any dispute arising out of or relating
                to them or the Service shall be governed by and construed in accordance with the laws
                of India, specifically the laws applicable in the state of Rajasthan, without regard to
                its conflict of law provisions.
              </Sub>
              <Sub n="18.2">
                <strong>Informal Resolution.</strong> Before initiating any formal legal proceedings,
                you agree to first attempt to resolve any dispute informally by contacting us at{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>. We
                will make a good-faith effort to resolve the dispute within thirty (30) days.
              </Sub>
              <Sub n="18.3">
                <strong>Jurisdiction.</strong> If the dispute is not resolved informally, it shall be
                subject to the exclusive jurisdiction of the competent courts located in Rajasthan,
                India. You consent to the personal jurisdiction of such courts and waive any objection
                to venue in such courts.
              </Sub>
              <Sub n="18.4">
                <strong>Consumer Rights.</strong> Nothing in these Terms limits or waives any
                rights you may have under mandatory consumer protection laws applicable in your
                jurisdiction that cannot be waived by contract. If such laws provide you with rights
                that conflict with these Terms, those mandatory rights shall prevail to the extent of
                the conflict.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 19: Miscellaneous
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s19" n="19" title="Miscellaneous" />
              <Sub n="19.1">
                <strong>Entire Agreement.</strong> These Terms, together with the Privacy Policy,
                constitute the entire agreement between you and Palindev concerning the Service and
                supersede all prior and contemporaneous agreements, representations, negotiations, and
                understandings, whether written or oral.
              </Sub>
              <Sub n="19.2">
                <strong>Severability.</strong> If any provision of these Terms is held by a court of
                competent jurisdiction to be invalid, illegal, or unenforceable, such provision shall
                be modified to the minimum extent necessary to make it enforceable, and the remaining
                provisions shall continue in full force and effect.
              </Sub>
              <Sub n="19.3">
                <strong>Waiver.</strong> Our failure to enforce any right or provision of these Terms
                in any instance shall not be construed as a waiver of such right or provision in any
                other instance. Any waiver must be in writing and signed by an authorized representative
                to be effective.
              </Sub>
              <Sub n="19.4">
                <strong>Assignment.</strong> You may not assign, transfer, delegate, or sublicense any
                of your rights or obligations under these Terms without our prior written consent. Any
                purported assignment in violation of this section is null and void. We may freely
                assign our rights and obligations without restriction.
              </Sub>
              <Sub n="19.5">
                <strong>Force Majeure.</strong> We shall not be liable to you for any failure or delay
                in performance resulting from causes beyond our reasonable control, including acts of
                God, natural disasters, war, terrorism, civil unrest, government action, power or
                internet outages, or failures of third-party service providers.
              </Sub>
              <Sub n="19.6">
                <strong>No Partnership.</strong> Nothing in these Terms shall be construed to create a
                partnership, joint venture, agency, employment, or franchise relationship between you
                and Palindev.
              </Sub>
              <Sub n="19.7">
                <strong>Language.</strong> These Terms are written in English. In the event of any
                discrepancy between an English version and a translated version of these Terms, the
                English version shall prevail.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 20: Contact Information
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s20" n="20" title="Contact Information" />
              <P>
                If you have any questions, concerns, or requests regarding these Terms or the Service,
                please contact us using the details below. We will make reasonable efforts to respond
                within a reasonable timeframe.
              </P>
              <Box className="tos-contact-box">
                <Typography variant="body2" fontWeight={800} sx={{ mb: 1.5, fontSize: 15 }}>
                  Palindev / NoteCORE
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">Email</Box>
                  <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">Website</Box>
                  <Link href="https://palindev.com" target="_blank" rel="noopener noreferrer" className="tos-link">
                    palindev.com
                  </Link>
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">App</Box>
                  <Link href={SITE_URL} target="_blank" rel="noopener noreferrer" className="tos-link">
                    {SITE_URL}
                  </Link>
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">Jurisdiction</Box>
                  Rajasthan, India
                </Typography>
              </Box>

              <Divider className="tos-divider" sx={{ mt: 5, mb: 3 }} />

              <Box className="tos-footer">
                <Typography variant="caption" className="tos-footer__copy">
                  &copy; {new Date().getFullYear()} Palindev / NoteCORE. All rights reserved.
                </Typography>
                <Box className="tos-footer__links">
                  <Link component={RouterLink} to="/privacy" className="tos-footer__link" underline="none">
                    Privacy Policy
                  </Link>
                  <Link component={RouterLink} to="/" className="tos-footer__link" underline="none">
                    Home
                  </Link>
                </Box>
              </Box>

            </Box>{/* /tos-content */}
          </Box>{/* /tos-layout */}
        </Container>
      </Box>
    </>
  );
};

export default TermsOfServicePage;
